import { create } from 'zustand';
import { http } from '@/libs/http';

export interface OnboardingRequest {
    id: string;
    email: string;
    requested_tenant_id: string | null;
    requested_app_id: string | null;
    requested_role_id: string | null;
    status: 'pending' | 'approved' | 'rejected';
    reason: string | null;
    reviewed_by: string | null;
    reviewed_at: string | null;
    created_at: string;
    tenant_name: string | null;
    app_name: string | null;
}

interface RawOnboardingRequest {
    id: string;
    email: string;
    tenant_id: string | null;
    metadata?: Record<string, unknown>;
    status: 'pending' | 'approved' | 'rejected' | 'expired';
    processed_by?: string | null;
    processed_at?: string | null;
    rejection_reason?: string | null;
    requested_at?: string;
    created_at?: string;
}

interface OnboardingListResponse {
    items: RawOnboardingRequest[];
}

const mapRequest = (raw: RawOnboardingRequest): OnboardingRequest => {
    const metadata = raw.metadata ?? {};
    const requestedRole = typeof metadata.requested_role_id === 'string' ? metadata.requested_role_id : null;
    const requestedApp = typeof metadata.requested_app_id === 'string' ? metadata.requested_app_id : null;
    const businessReason = typeof metadata.reason === 'string' ? metadata.reason : null;

    return {
        id: raw.id,
        email: raw.email,
        requested_tenant_id: raw.tenant_id,
        requested_app_id: requestedApp,
        requested_role_id: requestedRole,
        status: raw.status === 'expired' ? 'rejected' : raw.status,
        reason: raw.rejection_reason || businessReason,
        reviewed_by: raw.processed_by || null,
        reviewed_at: raw.processed_at || null,
        created_at: raw.created_at || raw.requested_at || new Date().toISOString(),
        tenant_name: null,
        app_name: null,
    };
};

interface OnboardingState {
    requests: OnboardingRequest[];
    loading: boolean;
    list: (status?: string) => Promise<void>;
    get: (id: string) => Promise<OnboardingRequest>;
    approve: (id: string) => Promise<void>;
    reject: (id: string, reason: string) => Promise<void>;
}

export const useOnboarding = create<OnboardingState>((set) => ({
    requests: [],
    loading: false,
    list: async (status) => {
        set({ loading: true });
        try {
            const params = status ? { status } : {};
            const data = await http.get<OnboardingListResponse>('/mgmt/v1/onboarding', { params });
            set({ requests: (data.items || []).map(mapRequest) });
        } finally {
            set({ loading: false });
        }
    },
    get: async (id) => {
        const data = await http.get<RawOnboardingRequest>(`/mgmt/v1/onboarding/${id}`);
        return mapRequest(data);
    },
    approve: async (id) => {
        await http.post(`/mgmt/v1/onboarding/${id}/approve`, {});
    },
    reject: async (id, reason) => {
        await http.post(`/mgmt/v1/onboarding/${id}/reject`, { rejection_reason: reason });
    },
}));
